/**
 * seo.js — lightweight per-route SEO management (no dependencies).
 *
 * A CRA SPA has one static index.html; Googlebot renders JS, so setting
 * document.title / meta / JSON-LD at route level still lands in the index.
 * (Non-JS crawlers only see public/index.html defaults + sitemap + llms.txt.)
 */
import { useEffect } from "react";

const SITE = "Ella Froze";
export const ORIGIN = "https://ellafroze.com";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ellafroze.app&hl=en";

// Google OAuth **Web** client ID — public (used by Google Identity Services in
// the browser). The client SECRET stays server-side only and is never shipped.
export const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "236369225591-nr7kjeom81qk6a10ciqmsorgfc1b9gqp.apps.googleusercontent.com";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const JSONLD_ID = "route-jsonld";
function setJsonLd(obj) {
  document.getElementById(JSONLD_ID)?.remove();
  if (!obj) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = JSONLD_ID;
  s.textContent = JSON.stringify(obj);
  document.head.appendChild(s);
}

const DEFAULT_OG_IMAGE = `${ORIGIN}/img/logo.png`;

/**
 * useSeo({ title, description, path, image, jsonLd })
 * - title: page title WITHOUT the site suffix
 * - path:  canonical path ("/product/123"); defaults to current pathname
 * - image: absolute og:image URL (defaults to the site logo). This covers
 *   JS-rendering crawlers (Googlebot); non-JS social scrapers are handled by
 *   the server-side share.php prerender (see README).
 * - jsonLd: object (or array) for a route-level JSON-LD block
 */
export function useSeo({ title, description, path, image, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE}` : `${SITE} — Frozen Food Halal & Higienis`;
    document.title = fullTitle;
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("name", "twitter:title", fullTitle);
    // OG images must be absolute URLs; product images come back same-origin ("/api/…").
    const rawImg = image || DEFAULT_OG_IMAGE;
    const ogImage = /^https?:\/\//.test(rawImg) ? rawImg : `${ORIGIN}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`;
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    const canonical = `${ORIGIN}${path ?? window.location.pathname}`;
    upsertCanonical(canonical);
    upsertMeta("property", "og:url", canonical);
    setJsonLd(jsonLd || null);
    return () => setJsonLd(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, JSON.stringify(jsonLd || null)]);
}

/** Product structured data for /product/:id (Google rich results + AI answers). */
export function productJsonLd(detail, images, productImgUrl, price) {
  if (!detail) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: detail.Product,
    image: (images?.length ? images : [{ ImagePath: detail.ImagePath }])
      .filter((im) => im.ImagePath)
      .map((im) => productImgUrl(im.ImagePath)),
    description: (detail.Description || detail.Product || "").slice(0, 500),
    brand: { "@type": "Brand", name: "Ella Froze" },
    offers: {
      "@type": "Offer",
      priceCurrency: "IDR",
      price: String(Math.round(price)),
      availability: Number(detail.Stock) > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      url: `${ORIGIN}/product/${detail.ProductID}`,
      seller: { "@type": "Organization", name: `Ella Froze ${detail.Branch || ""}`.trim() },
    },
  };
}

/** FAQPage structured data for /help. */
export function faqJsonLd(faqs) {
  const strip = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const items = (faqs || []).filter((f) => f.Title && strip(f.Content));
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.Title,
      acceptedAnswer: { "@type": "Answer", text: strip(f.Content).slice(0, 1000) },
    })),
  };
}
